/**
 * The backend refuses to CHANGE an order's invoice billing settings
 * (isRecurring, contractEndDate, invoiceScheduleMode, invoiceScheduleDay,
 * billingPeriodMode, billingStartDate) for a user without the sales-invoice
 * "manage" tier — see `utils/salesInvoicePermissions.ts` for the matching
 * frontend check, which disables these fields before the request is ever
 * sent; this is the fallback for whatever slips through (e.g. a stale form,
 * or another tab's permissions having changed underneath it).
 *
 * `orders/post` and `orders/patch` only run the check when the settings
 * actually changed, and report it as `403 { code: 'INVOICE_SETTINGS_FORBIDDEN' }`.
 * `PATCH orders/:id/invoice-settings` is gated by the `canManageSalesInvoices`
 * policy for every request to that route (it has nothing else to do), so it
 * 403s the same way but without a `code` at all — for that dedicated
 * endpoint a bare 403 already means this.
 */
export function isInvoiceSettingsForbiddenError(
  err: any,
  opts: { dedicatedEndpoint?: boolean } = {}
): boolean {
  if (err?.response?.data?.code === "INVOICE_SETTINGS_FORBIDDEN") return true;
  return !!opts.dedicatedEndpoint && err?.response?.status === 403;
}

/**
 * @returns a translated, ready-to-display message for an order save or
 * invoice-settings save failure — the permission 403 is mapped to a
 * friendlier explanation than the backend's raw text, everything else falls
 * back to the server's own message.
 */
export function resolveOrderSaveErrorMessage(
  err: any,
  t: (key: string, opts?: any) => string,
  opts: { dedicatedEndpoint?: boolean } = {}
): string {
  if (isInvoiceSettingsForbiddenError(err, opts)) {
    return t("invoice_settings_permission_error", {
      defaultValue:
        "Only users who manage sales invoices can change billing settings.",
    });
  }
  return (
    err?.response?.data?.message ||
    err?.message ||
    t("error", { defaultValue: "Error" })
  );
}
